import { FC, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./styles.scss";
import { Logo } from "../Logo";
import { LanguageToggle } from "../LanguageToggle";
import { MobileMenu } from "./MobileMenu";
import { LANDING_NAV_TABS, LANDING_DONATE_ITEM, LANDING_CONTACT_ITEM } from "../navConfig";
import { useTranslation } from "../../../hooks/useTranslation";

interface IProps {
  locale: string;
  onLanguageChange: (language: string) => void;
}

// Shared header for every landing page (hero `/` and the 7 tab/placeholder
// pages). Desktop shows the full tab row + action cluster; below 900px a
// hamburger replaces it and opens MobileMenu.
export const Header: FC<IProps> = ({ locale, onLanguageChange }) => {
  const { p } = useTranslation("landing_nav");
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="landing-header">
      <span className="landing-header__bh">ב״ה</span>

      <Link to="/" className="landing-header__logo-link" aria-label="ConnectYid home">
        <Logo />
      </Link>

      <nav className="landing-header__tabs" aria-label="Main">
        {LANDING_NAV_TABS.map((tab) => (
          <Link
            key={tab.key}
            to={tab.path}
            className={
              "landing-header__tab" +
              (location.pathname === tab.path ? " landing-header__tab--active" : "")
            }
          >
            {p(tab.key)}
          </Link>
        ))}
      </nav>

      <div className="landing-header__actions">
        <Link to="/login" className="landing-header__link">
          {p("log_in")}
        </Link>
        <Link to={LANDING_DONATE_ITEM.path} className="landing-header__donate-btn">
          {p("donate")}
        </Link>
        <LanguageToggle language={locale} onChange={onLanguageChange} />
      </div>

      <Link
        to={LANDING_CONTACT_ITEM.path}
        className="landing-header__contact-float"
        aria-label={p("contact_us")}
      >
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path
            d="M12 3.5c-4.97 0-9 3.36-9 7.5 0 2.31 1.26 4.38 3.24 5.77-.1.9-.46 2.13-1.44 3.36a.5.5 0 0 0 .55.79c1.9-.55 3.34-1.4 4.3-2.1.75.14 1.53.21 2.35.21 4.97 0 9-3.36 9-7.03s-4.03-7.5-9-7.5Z"
            fill="#fff"
          />
          <circle cx="8.3" cy="11" r="1.15" fill="#2952c8" />
          <circle cx="12" cy="11" r="1.15" fill="#2952c8" />
          <circle cx="15.7" cy="11" r="1.15" fill="#2952c8" />
        </svg>
      </Link>

      <button
        type="button"
        className={"landing-header__hamburger" + (mobileOpen ? " landing-header__hamburger--open" : "")}
        aria-label={mobileOpen ? "Close menu" : "Open menu"}
        aria-expanded={mobileOpen}
        // useClickOutside listens for mousedown on document.body to close the
        // menu — since this button sits outside the panel, its own mousedown
        // would close the menu a beat before this onClick re-opens it (net
        // effect: clicking to close silently reopens it). Stop it from
        // reaching that listener.
        onMouseDown={(e) => e.stopPropagation()}
        onClick={() => setMobileOpen((prev) => !prev)}
      >
        <span />
        <span />
        <span />
      </button>

      <MobileMenu
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        locale={locale}
        onLanguageChange={onLanguageChange}
      />
    </header>
  );
};
